import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@amf/shared/components/form/TextField';
import Button from '@amf/shared/components/button/Button';

const schema = yup.object({
  name: yup
    .string()
    .required('join.league.form.name.required')
    .max(64, 'join.league.form.name.max'),
  city: yup
    .string()
    .required('join.league.form.city.required')
    .max(64, 'join.league.form.city.max'),
  email: yup
    .string()
    .email('join.league.form.email.invalid')
    .required('join.league.form.email.required'),
  phone: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g, 'join.league.form.phone.invalid')
    .required('join.league.form.phone.required'),
});

export interface JoinLeagueFormData {
  name: string;
  city: string;
  email: string;
  phone: string;
}

interface JoinLeagueFormProps {
  values: JoinLeagueFormData;
  onSubmit: (values: JoinLeagueFormData) => Promise<void>;
}

function JoinLeagueForm({ values, onSubmit }: JoinLeagueFormProps) {
  const { t } = useTranslate('malyfotbal');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<JoinLeagueFormData>({
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('joinLeagueForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: JoinLeagueFormData) => {
      setSending(true);
      await onSubmit(data);
      setSending(false);
    },
    [onSubmit],
  );

  return (
    <div className='Form'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.league.form.name')}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='city'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.league.form.city')}
                  error={errors.city?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.league.form.email')}
                  error={errors.email?.message}
                  type='email'
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.team.form.phone')}
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Button type='submit' variant='blue' disabled={sending}>
              {t('join.league.form.send')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default JoinLeagueForm;
